import { createContext, useContext, useState, useEffect, ReactNode } from 'react'

export interface CookiePreferences {
  essential: boolean // Always true, non-negotiable
  functional: boolean // Remember preferences, settings
  analytics: boolean // Matomo analytics
  marketing: boolean // Marketing cookies (if any in future)
  timestamp: number
  version: string
}

interface CookieConsentContextType {
  preferences: CookiePreferences | null
  hasConsent: boolean
  updatePreferences: (prefs: Partial<CookiePreferences>) => void
  acceptAll: () => void
  rejectAll: () => void
  resetConsent: () => void
}

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined)

const STORAGE_KEY = 'demirix-cookie-preferences'
const CONSENT_VERSION = '1.0'

const DEFAULT_PREFERENCES: CookiePreferences = {
  essential: true,
  functional: false,
  analytics: false,
  marketing: false,
  timestamp: Date.now(),
  version: CONSENT_VERSION
}

export const CookieConsentProvider = ({ children }: { children: ReactNode }) => {
  const [preferences, setPreferences] = useState<CookiePreferences | null>(null)
  const [hasConsent, setHasConsent] = useState(false)

  useEffect(() => {
    // Load preferences from localStorage
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as CookiePreferences
        // Check if version matches, if not, reset consent
        if (parsed.version === CONSENT_VERSION) {
          setPreferences(parsed)
          setHasConsent(true)
        } else {
          // Version mismatch, reset consent
          localStorage.removeItem(STORAGE_KEY)
          // Also remove old consent key
          localStorage.removeItem('demirix-cookie-consent')
        }
      } catch {
        // Failed to parse cookie preferences - reset to defaults
      }
    }
  }, [])

  const savePreferences = (prefs: CookiePreferences) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs))
    setPreferences(prefs)
    setHasConsent(true)
  }

  const updatePreferences = (partial: Partial<CookiePreferences>) => {
    const updated: CookiePreferences = {
      ...DEFAULT_PREFERENCES,
      ...partial,
      essential: true, // Always true
      timestamp: Date.now(),
      version: CONSENT_VERSION
    }
    savePreferences(updated)
  }

  const acceptAll = () => {
    const prefs: CookiePreferences = {
      essential: true,
      functional: true,
      analytics: true,
      marketing: true,
      timestamp: Date.now(),
      version: CONSENT_VERSION
    }
    savePreferences(prefs)
  }

  const rejectAll = () => {
    const prefs: CookiePreferences = {
      essential: true, // Essential cookies cannot be rejected
      functional: false,
      analytics: false,
      marketing: false,
      timestamp: Date.now(),
      version: CONSENT_VERSION
    }
    savePreferences(prefs)
  }

  const resetConsent = () => {
    localStorage.removeItem(STORAGE_KEY)
    localStorage.removeItem('demirix-cookie-consent') // Remove old key
    setPreferences(null)
    setHasConsent(false)
  }

  return (
    <CookieConsentContext.Provider
      value={{
        preferences,
        hasConsent,
        updatePreferences,
        acceptAll,
        rejectAll,
        resetConsent
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext)
  if (context === undefined) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider')
  }
  return context
}
